import * as http from 'http';
import response from './response';
import error from './error';
import createServer, { Server } from './server';
import { StaticActive } from './server.static';
import MapApi, * as Map from './map';
import RoleApi, * as Role from './role';
import LinkApi, * as Link from './link';
import RouterApi, * as Router from './router';
import UrlApi, * as Url from './url';
import initInfo, { Info, MiniInfo, ExtendedObject, AppExtendedObject } from './info';
import { CookieOpt } from './server.cookie';
import { File, saveFile, getUrl, getPath,  } from './server.multipart';
import { server as virtualServer } from './server.virtual';
import { pathMatch, init as initMatch } from './util.path-match';
import { Readable } from 'stream';
export { File, Map, Role, Link, Router, Url, Server, virtualServer, MiniInfo as BaseInfo, MiniInfo, Info, response as Response, error as Error, }

type Optional<item extends {[key: string]: any}> = {
	[key in keyof item]?: item[key] extends {[key: string]: any} ? (item[key] extends Function ? item[key] : Optional<item[key]> ) : item[key];
}

/** 用户管理 */
export interface UserManager<Ext extends ExtendedObject = {user: object}> {
	/** token 使用的 cookie */
	tokenName: string,
	/** 用于设置登陆用户的方法 */
	login(uid: string, config: object, info: Info<Ext>): string | boolean | Promise<string | boolean>;
	/** 用于退出用户的方法 */
	exit(config: object, info: Info<Ext>): boolean | Promise<boolean>;
	/** 用于判定当前用户是否登陆的方法 */
	checkCookie(cookie: string, config: object, info: Info<Ext>): string | [string] | [string, string] | Promise<string | [string] | [string, string]>;
	/** 获取用户权限 */
	getPower(uid: string): any;
}
export type BaseUserManager<Ext extends ExtendedObject = {user: object}> = Optional<UserManager<Ext>>;
function getUserManager<Ext extends ExtendedObject = {user: object}>(cfg: BaseUserManager<Ext>): UserManager<Ext> {
	let tokenName = 'user-token';
	let login: UserManager<Ext>['login'] = () => false;
	let exit: UserManager<Ext>['exit'] = () => false;
	let checkCookie: UserManager<Ext>['checkCookie'] = () => '';
	let getPower: UserManager<Ext>['getPower'] = () => {};
	
	const config: UserManager<Ext> = {
		get tokenName() {return tokenName;},
		get login() {return login;},
		get exit() {return exit;},
		get checkCookie() {return checkCookie;},
		get getPower() {return getPower;},

		set tokenName(v) { if (typeof v === 'string') { tokenName =  v; } },
		set login(v) { if (typeof v === 'function') { login =  v; } else if (v === null) { login = () => false; } },
		set exit(v) { if (typeof v === 'function') { exit =  v; } else if (v === null) { exit = () => false; } },
		set checkCookie(v) { if (typeof v === 'function') { checkCookie =  v; } else if (v === null) { checkCookie = () => ''; } },
		set getPower(v) { if (typeof v === 'function') { getPower =  v; } },
	};
	cfg = cfg || {};
	config.tokenName = cfg.tokenName || tokenName;
	config.login = cfg.login || login;
	config.exit = cfg.exit || exit;
	config.checkCookie = cfg.checkCookie || checkCookie;
	config.getPower = cfg.getPower || getPower;
	return config;
}
/** 文件保存相关 API */
export interface FileCfg {
	save: saveFile;
	getUrl: getUrl;
	getPath: getPath;
}
export type BaseFileCfg = Optional<FileCfg>;
function getFileCfg(cfg: BaseFileCfg): FileCfg {
	let save: saveFile = () => '';
	let getUrl: getUrl = () => '';
	let getPath: getPath = () => '';

	const config: FileCfg = {
		get save() { return save; },
		get getUrl() { return getUrl; },
		get getPath() { return getPath; },

		set save(v) { if (typeof v === 'function') { save = v; } else if (v === null) { save = () => ''; } },
		set getUrl(v) { if (typeof v === 'function') { getUrl = v; } else { getUrl = () => '' } },
		set getPath(v) { if (typeof v === 'function') { getPath = v; } else { getPath = () => ''; } },
	};
	cfg = cfg || {};
	config.save = cfg.save || save;
	config.getUrl = cfg.getUrl || getUrl;
	config.getPath = cfg.getPath || getPath;
	return config;
}

/** 日志相关 API */
/** 有连接 */
export interface LinkLog { (method: string, path: string, header: http.IncomingHttpHeaders): void; }
/** 产生错误 */
export interface ErrorLog { (error: Error): void; }
/** 处理结果 */
export interface HandleLog { (beginTime: Date, statusCode: number, type: string, method: string, path: string, header: http.IncomingHttpHeaders): void; }
export interface LogCfg {
	link: LinkLog;
	error: ErrorLog;
	handle: HandleLog;
}
export type BaseLogCfg = Optional<LogCfg>;
function getLogCfg(cfg: BaseLogCfg): LogCfg {
	let link: LinkLog = () => undefined;
	let error: ErrorLog = () => undefined;
	let handle: HandleLog = () => undefined;
	const config = {
		get link() { return link; },
		get error() { return error; },
		get handle() { return handle; },
		set link(f) { if (typeof f === 'function') { link = f; } },
		set error(f) { if (typeof f === 'function') { error = f; } },
		set handle(f) { if (typeof f === 'function') { handle = f; } },
	}
	cfg = cfg || {};
	config.link = cfg.link || link;
	config.error = cfg.error || error;
	config.handle = cfg.handle || handle;
	return config;
}

/** 静态相关 API */
export interface StaticCfg {
	basePath(appId: string): string | string[];
	index(accept: string[]): string | string[];
	extension(accept: string[]): string | string[];
	active?: StaticActive;
}
export type BaseStaticCfg = Optional<StaticCfg>;
function getStaticCfg(cfg: BaseStaticCfg): StaticCfg {
	let basePath: (appId: string) => string | string[] = (appId) => [appId ? `app/${appId}/public/` : `public/`];
	let index: (accept: string[]) => string | string[] = () => ['index.html'];
	let extension: (accept: string[]) => string | string[] = () => ['.html'];
	let active: undefined | StaticActive;

	const config = {
		get basePath() { return basePath; },
		get index() { return index; },
		get extension() { return extension; },
		get active() { return active; },
		set basePath(f) { if (typeof f === 'function') { basePath = f; } },
		set index(f) { if (typeof f === 'function') { index = f; } },
		set extension(f) { if (typeof f === 'function') { extension = f; } },
		set active(f) { active = typeof f === 'function' ? f : undefined; },
	}
	cfg = cfg || {};
	config.basePath = cfg.basePath || basePath;
	config.index = cfg.index || index;
	config.extension = cfg.extension || extension;
	config.active = cfg.active;
	return config;
}

export type BufferType = {buffer: ArrayBuffer | SharedArrayBuffer} | ArrayBuffer | SharedArrayBuffer | Buffer;
/** 页面渲染接口 */
export interface Render {
	(data: object, path: string, appId?: string): BufferType | string | Readable | Promise<BufferType | string | Readable>
}
/** Info 扩展接口 */
export interface Extends {
	(appId?: string): AppExtendedObject | Promise<AppExtendedObject>
}
/** 配置 */
export interface Config<Ext extends ExtendedObject = {user: object}> {
	/** 是否为调试模式 */
	isDebug: boolean;
	/** 默认端口号 */
	port: number;
	/** 默认协议 */
	protocol: string;
	/** jsonp 回调函数名称 */
	cbName: string;
	/** cookie 选项 */
	cookie: CookieOpt;
	/** 动态渲染函数 */
	render?: Render;
	/** Info 扩展接口 */
	extends: Extends;
	/** 用户管理 */
	userManager: UserManager<Ext>;
	/** 静态管理 */
	static: StaticCfg;
	/** 文件相关协议 */
	file: FileCfg;
	util: Ext['util'];
	api: Ext['api'];
	userApi: (uid: string) => Ext['user'] | null;
	log: LogCfg;
}
export type BaseConfig<Ext extends ExtendedObject = {user: object}> = Optional<Config<Ext>>;
function getConfig<Ext extends ExtendedObject = {user: object}>(cfg: BaseConfig<Ext>): Config<Ext> {
	let isDebug = false;
	let port = 0;
	let protocol = '';
	let cbName = '';
	let cookie = {};
	let userManager: BaseUserManager<Ext> = {};
	let staticCfg: BaseStaticCfg = {};
	let file: BaseFileCfg = {};
	let util: Ext['util'];
	let api: Ext['api'];
	let log: LogCfg;
	let userApi: (uid: string) => Ext['user'] | null = () => null;
	let render: Render | undefined;
	let extendsInt: Extends = () => ({});
	const config: Config<Ext> = {
		get isDebug() { return isDebug; },
		get port() { return port; },
		get protocol() { return protocol; },
		get cbName() { return cbName; },
		get cookie() { return cookie; },
		get userManager() { return userManager as UserManager<Ext>; },
		get static() { return staticCfg as StaticCfg; },
		get file() { return file as FileCfg; },
		get log() { return log; },
		get util() { return util; },
		get api() { return api; },
		get render() { return render; },
		get extends() { return extendsInt; },
		get userApi() { return userApi; },

		set isDebug(v) { isDebug = Boolean(v); },
		set port(v) { if (v === 0 || (v = Number(v))) { port = v; } },
		set protocol(v) { if (typeof v === 'string') { protocol =  v; } },
		set cbName(v) { if (typeof v === 'string') { cbName =  v; } },
		set cookie(v) { if (typeof v === 'object') { cookie =  v; } },
		set userManager(v) { if (typeof v === 'object') { userManager = getUserManager(v); } },
		set static(v) { if (typeof v === 'object') { staticCfg = getStaticCfg(v); } },
		set file(v) { if (typeof v === 'object') { file = getFileCfg(v); } },
		set log(v) { if (typeof v === 'object') { log = getLogCfg(v); } },
		set util(v) { util = v; },
		set api(v) { api = v; },
		set render(v) { if (typeof v === 'function' || v === undefined ) { render = v; } },
		set extends(v) { if (typeof v === 'function') { extendsInt = v; } },
		set userApi(v) { if (typeof v === 'function') { userApi = v; } },
	};
	cfg = cfg || {};
	config.isDebug = cfg.isDebug || isDebug;
	config.port = cfg.port || port;
	config.protocol = cfg.protocol || protocol;
	config.cbName = cfg.cbName || cbName;
	config.cookie = cfg.cookie || cookie;
	config.render = cfg.render;
	config.extends = cfg.extends || extendsInt;
	config.userApi = cfg.userApi || userApi;
	userManager = getUserManager(cfg.userManager || {});
	staticCfg = getStaticCfg(cfg.static || {});
	file = getFileCfg(cfg.file || {});
	log = getLogCfg(cfg.log || {});
	util = cfg.util as Ext['util'];
	api = cfg.api as Ext['api'];
	return config;
}
interface xurouter<Ext extends ExtendedObject = {user: object}> {
	readonly map: MapApi;
	readonly role: RoleApi;
	readonly link: LinkApi<Ext>;
	readonly router: RouterApi<Ext>;
	readonly Response: typeof response;
	readonly Error: typeof error;
	readonly url: UrlApi;
	readonly server: Server;
	match: pathMatch;

	readonly config: Config<Ext>;
}
function xurouter<Ext extends ExtendedObject = {user: object}>(cfg?: BaseConfig<Ext>): xurouter<Ext> {
	const config = getConfig(cfg || {});
	const map = MapApi();
	const role = RoleApi();
	const link = LinkApi<Ext>();
	const router = RouterApi<Ext>();

	const url = UrlApi(config, map.find, role.get);
	const {recursion, match} = initMatch(config, map.list, link.get);
	const info = initInfo(config, recursion, url.get, role.get, router.get);

	const ret = {
		map, role, link, router, url,
		Response: response,
		Error: error,

		match,
		server: createServer(config, match, router.get, info),

		create:router.create,
		set:router.set,
		remove:router.remove,
		get:router.get,

		getUrl: url.get,

		setMap: map.set,
		removeMap: map.remove,
		findMap: map.find,
		mapList: map.list,
		updateMap: map.update,

		setLink: link.set,
		removeLink: link.remove,
		getLink: link.get,

		get config() { return config; },
	}
	return ret;
}

export default xurouter;
